import Bugsnag from '@bugsnag/js';
import memoizeOne from 'memoize-one';
import {
  Combiner,
  createSelectorCreator as reselectCreateSelectorCreator,
  GetParamsFromSelectors,
  GetStateFromSelectors,
  Selector,
  SelectorArray,
  SelectorResultArray,
  UnknownMemoizer,
} from 'reselect';
import {Observable, Subject} from 'rxjs';

import {BugsnagSeveritiesEnum} from '../../helpers/bugsnag/bugsnagSeverities';
import {maybeMeasureFunctionDuration} from '../../helpers/instrumentation/instrumentationHelpers';
import {DropFirstParameter} from '../../helpers/typescript/typeHelpers';
import {reportToBugsnag} from '../../services/bugsnag/bugsnag';

// These checks were implemented in Reselect v5. Temporarily disabled to avoid
// flooding the console with warnings, and because they break some tests because
// they run selectors twice in development.
const GLOBAL_CREATE_SELECTOR_OPTIONS = {
  devModeChecks: {
    identityFunctionCheck: 'never',
    inputStabilityCheck: 'never',
  },
} as const;

let isInstrumentationEnabled = false;

export function enableSelectorInstrumentation() {
  isInstrumentationEnabled = true;
}

export function disableSelectorInstrumentation() {
  isInstrumentationEnabled = false;
}

export interface SelectorComputationResult {
  selectorName: string;
  duration: number;
  startTime: number;
  lastCombinerDuration: number;
  totalSelectorTime: number;
  didRecompute: boolean;
  recomputationCount: number;
  invocationCount: number;
}

export const selectorComputationsSubject$ = new Subject<SelectorComputationResult>();
export const selectorComputations$: Observable<SelectorComputationResult> = selectorComputationsSubject$;

export interface CreateSelectorOptions {
  selectorName?: string;
}

export interface FrontCreateSelectorFunction {
  <InputSelectors extends SelectorArray<any>, Result>(
    inputSelectors: [...InputSelectors],
    combiner: Combiner<InputSelectors, Result>,
    options?: CreateSelectorOptions,
  ): Selector<GetStateFromSelectors<InputSelectors>, Result, GetParamsFromSelectors<InputSelectors>>;
}

export function instrumentedCreateSelectorCreator<MemoizeFunction extends UnknownMemoizer>(
  memoize: MemoizeFunction,
  ...memoizeOptionsFromArgs: DropFirstParameter<MemoizeFunction>
): FrontCreateSelectorFunction {
  const uninstrumentedCreateSelector = reselectCreateSelectorCreator<MemoizeFunction>(
    memoize,
    ...memoizeOptionsFromArgs,
  );

  return <InputSelectors extends SelectorArray<any>, Result>(
    selectors: [...InputSelectors],
    combiner: Combiner<InputSelectors, Result>,
    options?: CreateSelectorOptions,
  ) => {
    if (!options) {
      // This may happen if our SWC (web) or Babel (mobile) plugins fail to add the selector name.
      // This should not happen in production, but let's preserve the behavior to be
      // defensive.
      reportMissingSelectorName(Bugsnag.isStarted());

      return uninstrumentedCreateSelector(selectors, combiner, GLOBAL_CREATE_SELECTOR_OPTIONS);
    }

    let lastCombinerDuration = 0;
    let lastRecomputationCount = 0;
    let invocationCount = 0;
    let totalSelectorTime = 0;
    const wrappedCombiner = (...args: SelectorResultArray<InputSelectors>) => {
      const onMeasureCombinerDuration = (duration: number) => {
        lastCombinerDuration = duration;
      };
      return maybeMeasureFunctionDuration(
        () => combiner(...args),
        isInstrumentationEnabled ? onMeasureCombinerDuration : undefined,
      );
    };
    const selector = uninstrumentedCreateSelector(selectors, wrappedCombiner, GLOBAL_CREATE_SELECTOR_OPTIONS);

    const wrappedSelector = (
      state: Parameters<typeof selector>[0],
      ...params: DropFirstParameter<typeof selector>
    ) => {
      const onMeasureSelectorDuration = (duration: number, startTime: number) => {
        totalSelectorTime += duration;
        invocationCount += 1;

        const summary = {
          selectorName: options?.selectorName || 'Unknown selector name',
          startTime,
          duration,
          lastCombinerDuration,
          totalSelectorTime,
          didRecompute: selector.recomputations() - lastRecomputationCount === 1,
          recomputationCount: selector.recomputations(),
          invocationCount,
        };
        selectorComputationsSubject$.next(summary);

        lastRecomputationCount = selector.recomputations();
      };
      return maybeMeasureFunctionDuration(
        () => selector(state, ...params),
        isInstrumentationEnabled ? onMeasureSelectorDuration : undefined,
      );
    };

    return wrappedSelector;
  };
}

// Memoized so we don't report more than once per session.
// Keep in mind that selectors created before the logger is initialized are only reported in the console.
const reportMissingSelectorName = memoizeOne((_isBugsnagInitialized: boolean) => {
  reportToBugsnag(new Error('Missing selector name.'), {severity: BugsnagSeveritiesEnum.INFO});
});
